// src/components/forms/RegistryLinkInput.tsx

import { useInput, useRecordContext, useTranslate } from 'react-admin';
import { Box, Chip, FormHelperText, InputLabel } from '@mui/material';
import { DirectoryPicker } from '@roommatik/directory-client';
import '@roommatik/directory-client/style.css';
import { API_CONFIG } from '@/config/constants';
import keycloak from '@/lib/keycloak';

const PICKER_PATH = `${API_CONFIG.BASE_URL}/directory/picker`;

const getToken = async () => {
  if (keycloak.isTokenExpired(30)) {
    try {
      await keycloak.updateToken(30);
    } catch {
      // A failed refresh surfaces as a 401 that the picker reports as unavailable.
    }
  }
  return keycloak.token;
};

interface RegistryLinkInputProps {
  source: string;
  kind: 'property' | 'organization';
  label: string;
}

/**
 * Links a hotel or group to its record in the shared registry. Creating a record is not offered:
 * the person chooses one that already exists. Once linked, the link is read-only.
 */
export const RegistryLinkInput = ({ source, kind, label }: RegistryLinkInputProps) => {
  const translate = useTranslate();
  const record = useRecordContext();
  const { field } = useInput({ source });
  const locked = Boolean(record?.[source]);

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      <InputLabel shrink sx={{ position: 'static', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        {label}
        {locked && <Chip size="small" color="success" variant="outlined" label={translate('common.linked')} />}
      </InputLabel>
      <DirectoryPicker
        kind={kind}
        value={field.value ?? null}
        onChange={(picked) => field.onChange(picked?.id ?? null)}
        basePath={PICKER_PATH}
        getToken={getToken}
        canCreate={false}
        disabled={locked}
        aria-label={label}
      />
      <FormHelperText>
        {translate(locked ? 'common.registryLinkLocked' : 'common.registryLinkOptional')}
      </FormHelperText>
    </Box>
  );
};
